import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import Icon from "../../../components/common/Icon";
import styles from "./PromptVoice.module.css";

interface PromptVoiceDesignProps {
    onGenerate: (params: {
        voiceDescription: string;
        previewText: string;
        autoGenerateText: boolean;
        loudness: number;
        guidanceScale: number;
    }) => Promise<void>;
    isGenerating: boolean;
    disabled: boolean;
}

const PromptVoiceDesign: React.FC<PromptVoiceDesignProps> = ({ onGenerate, isGenerating, disabled }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [voiceDescription, setVoiceDescription] = useState("");
    const [previewText, setPreviewText] = useState("");
    const [autoGenerateText, setAutoGenerateText] = useState(true);
    const [loudness, setLoudness] = useState(0.5);
    const [guidanceScale, setGuidanceScale] = useState(5);

    const minChars = 20;
    const maxChars = 1000;
    const previewTextMinChars = 100;
    const previewTextMaxChars = 1000;

    const isValidVoiceDescription = voiceDescription.length >= minChars && voiceDescription.length <= maxChars;
    const isValidPreviewText =
        autoGenerateText || (previewText.length >= previewTextMinChars && previewText.length <= previewTextMaxChars);
    const isValidForm = isValidVoiceDescription && isValidPreviewText;

    const getDisabledReason = (): string => {
        if (voiceDescription.length < minChars) {
            return (
                t("voiceDescriptionTooShort", { count: minChars }) ||
                `Voice description must be at least ${minChars} characters`
            );
        }
        if (voiceDescription.length > maxChars) {
            return (
                t("voiceDescriptionTooLong", { count: maxChars }) ||
                `Voice description must be at most ${maxChars} characters`
            );
        }
        if (!autoGenerateText && previewText.length < previewTextMinChars) {
            return (
                t("previewTextTooShort", { count: previewTextMinChars }) ||
                `Preview text must be at least ${previewTextMinChars} characters`
            );
        }
        if (!autoGenerateText && previewText.length > previewTextMaxChars) {
            return (
                t("previewTextTooLong", { count: previewTextMaxChars }) ||
                `Preview text must be at most ${previewTextMaxChars} characters`
            );
        }
        return "";
    };

    const handleGenerate = async () => {
        if (!isValidForm || disabled) return;
        await onGenerate({
            voiceDescription,
            previewText,
            autoGenerateText,
            loudness,
            guidanceScale,
        });
    };

    return (
        <div>
            {/* Design Tips */}
            <div className={styles.cloneVoiceTips}>
                <div className={styles.tipCard}>
                    <Icon name="ic-file-text" className={styles.tipIcon} />
                    <h4>{t("promptVoiceTip1Title")}</h4>
                    <p>{t("promptVoiceTip1Description")}</p>
                </div>
                <div className={styles.tipCard}>
                    <Icon name="ic-mic" className={styles.tipIcon} />
                    <h4>{t("promptVoiceTip2Title")}</h4>
                    <p>{t("promptVoiceTip2Description")}</p>
                </div>
                <div className={styles.tipCard}>
                    <Icon name="ic-smile" className={styles.tipIcon} />
                    <h4>{t("promptVoiceTip3Title")}</h4>
                    <p>{t("promptVoiceTip3Description")}</p>
                </div>
            </div>

            {/* Design Form */}
            <div className={styles.uploadSection}>
                <div className={styles.formGroup}>
                    <label htmlFor="voiceDescription" className={styles.formLabel}>
                        {t("voiceDescription")} *
                    </label>
                    <textarea
                        id="voiceDescription"
                        className={styles.formTextarea}
                        placeholder={t("voiceDescriptionPlaceholder")}
                        value={voiceDescription}
                        onChange={(e) => setVoiceDescription(e.target.value)}
                        rows={5}
                        disabled={disabled || isGenerating}
                    />
                    <div className={styles.characterCount}>
                        <span
                            className={
                                voiceDescription.length > maxChars || voiceDescription.length < minChars
                                    ? styles.invalid
                                    : ""
                            }
                        >
                            {voiceDescription.length} / {maxChars}
                        </span>
                        <span className={styles.hint}>{t("minimumCharacters", { count: minChars })}</span>
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={autoGenerateText}
                            onChange={(e) => setAutoGenerateText(e.target.checked)}
                            disabled={disabled || isGenerating}
                        />
                        {t("autoGeneratePreviewText")}
                    </label>
                </div>

                {!autoGenerateText && (
                    <div className={styles.formGroup}>
                        <label htmlFor="previewText" className={styles.formLabel}>
                            {t("previewText")}
                        </label>
                        <textarea
                            id="previewText"
                            className={styles.formTextarea}
                            placeholder={t("previewTextPlaceholder")}
                            value={previewText}
                            onChange={(e) => setPreviewText(e.target.value)}
                            rows={3}
                            disabled={disabled || isGenerating}
                        />
                        <div className={styles.characterCount}>
                            <span
                                className={
                                    previewText.length > previewTextMaxChars || previewText.length < previewTextMinChars
                                        ? styles.invalid
                                        : ""
                                }
                            >
                                {previewText.length} / {previewTextMaxChars}
                            </span>
                            <span className={styles.hint}>
                                {t("minimumCharacters", { count: previewTextMinChars })}
                            </span>
                        </div>
                    </div>
                )}

                <div className={styles.sliderGroup}>
                    <div className={styles.formGroup}>
                        <label htmlFor="loudness" className={styles.formLabel}>
                            {t("loudness")}: {loudness.toFixed(1)}
                        </label>
                        <input
                            type="range"
                            id="loudness"
                            min="-1"
                            max="1"
                            step="0.1"
                            value={loudness}
                            onChange={(e) => setLoudness(parseFloat(e.target.value))}
                            disabled={disabled || isGenerating}
                        />
                        <span className={styles.hint}>{t("loudnessHint")}</span>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="guidanceScale" className={styles.formLabel}>
                            {t("guidanceScale")}: {guidanceScale}
                        </label>
                        <input
                            type="range"
                            id="guidanceScale"
                            min="0"
                            max="100"
                            step="1"
                            value={guidanceScale}
                            onChange={(e) => setGuidanceScale(parseInt(e.target.value))}
                            disabled={disabled || isGenerating}
                        />
                        <span className={styles.hint}>{t("guidanceScaleHint")}</span>
                    </div>
                </div>

                <Tooltip title={!isValidForm && !disabled && !isGenerating ? getDisabledReason() : ""} arrow>
                    <span style={{ display: "block", width: "100%" }}>
                        <button
                            className={styles.primaryButton}
                            onClick={handleGenerate}
                            disabled={!isValidForm || disabled || isGenerating}
                        >
                            {isGenerating ? (
                                <>
                                    <svg
                                        className={styles.spinner}
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                    >
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                            strokeDasharray="32"
                                            strokeDashoffset="32"
                                        >
                                            <animate
                                                attributeName="stroke-dashoffset"
                                                values="32;0"
                                                dur="1s"
                                                repeatCount="indefinite"
                                            />
                                        </circle>
                                    </svg>
                                    {t("generatingPreviews")}...
                                </>
                            ) : (
                                t("generatePreviews")
                            )}
                        </button>
                    </span>
                </Tooltip>
            </div>
        </div>
    );
};

export default PromptVoiceDesign;
